import { QueryClient, QueryKey } from "@tanstack/react-query";
import {
  MutationConfig,
  QueryConfig,
  TanstackQueryCraftRegistry,
} from "./types";
import { tanstackQueryCraft } from "./core";

/**
 * Returns a set of helpers to invalidate queries using a provided
 * `QueryClient` instance. The returned object includes methods to:
 * - `invalidate`: invalidate a single registered query (optionally as the
 *   parent by using `asParent`).
 * - `invalidateByPrefix`: invalidate queries that match a given prefix
 *   `QueryKey`.
 * - `invalidateAll`: invalidate all queries in the client.
 * - `invalidateBulk`: invalidate multiple registered queries in parallel.
 *
 * This helper centralizes invalidation logic so callers can reference
 * registered module/query definitions instead of constructing query keys
 * manually.
 *
 * @param queryClient - The TanStack `QueryClient` used to perform
 *   invalidation operations.
 */
export function invalidateQueries(queryClient: QueryClient) {
  return {
    invalidate: <
      TModuleName extends keyof TanstackQueryCraftRegistry & string,
      TKey extends keyof TanstackQueryCraftRegistry[TModuleName] & string,
      TOptions = TanstackQueryCraftRegistry[TModuleName][TKey] extends (
        options?: infer O
      ) => any
        ? O
        : never
    >(
      moduleName: TModuleName,
      key: TKey,
      options?: TOptions & {
        asParent?: boolean;
      }
    ) => {
      const mergedOptions = {
        ...options,
        asParent: options?.asParent ?? false,
      };

      const config = getRegisteredQuery(moduleName, key, mergedOptions) as
        | QueryConfig
        | MutationConfig;

      if (config.type === "query") {
        return queryClient.invalidateQueries({
          queryKey: mergedOptions.asParent
            ? config.queryKey.slice(0, config.queryKey.length - 1)
            : config.queryKey,
        });
      }

      return Promise.resolve();
    },

    invalidateByPrefix: (prefix: QueryKey) => {
      return queryClient.invalidateQueries({ queryKey: prefix });
    },

    invalidateAll: () => {
      return queryClient.invalidateQueries();
    },

    invalidateBulk: <
      TModuleName extends keyof TanstackQueryCraftRegistry & string,
      TKey extends keyof TanstackQueryCraftRegistry[TModuleName] & string,
      TOptions = TanstackQueryCraftRegistry[TModuleName][TKey] extends (
        options?: infer O
      ) => any
        ? O
        : never
    >(
      queries: Array<{
        moduleName: TModuleName;
        key: TKey;
        options?: TOptions & {
          asParent?: boolean;
        };
      }>
    ) => {
      return Promise.all(
        queries.map(({ moduleName, key, options }) => {
          const mergedOptions = {
            ...options,
            asParent: options?.asParent ?? false,
          };
          const config = getRegisteredQuery(moduleName, key, mergedOptions) as
            | QueryConfig
            | MutationConfig;

          if (config.type === "query") {
            return queryClient.invalidateQueries({
              queryKey: mergedOptions.asParent
                ? config.queryKey.slice(0, config.queryKey.length - 1)
                : config.queryKey,
            });
          }

          return Promise.resolve();
        })
      );
    },

    queryClient,
  };
}

/**
 * Retrieve a registered query or mutation configuration from the global
 * `tanstackQueryCraft` registry.
 *
 * This function forwards the `moduleName`, `key` and optional `options`
 * to the registry and returns the resolved configuration or factory
 * result. Type generics ensure the return type matches the registered
 * definition.
 *
 * @param moduleName - The registered module name.
 * @param key - The key of the query or mutation within the module.
 * @param options - Optional options that may be passed to the registered
 *   factory function when resolving the configuration.
 */
export function getRegisteredQuery<
  TModuleName extends keyof TanstackQueryCraftRegistry & string,
  TKey extends keyof TanstackQueryCraftRegistry[TModuleName] & string,
  TOptions = TanstackQueryCraftRegistry[TModuleName][TKey] extends (
    options?: infer O
  ) => any
    ? O
    : never
>(
  moduleName: TModuleName,
  key: TKey,
  options?: TOptions
): TanstackQueryCraftRegistry[TModuleName][TKey] extends (
  options?: any
) => infer R
  ? R
  : never {
  return tanstackQueryCraft.get(moduleName, key, options);
}

/**
 * Return the list of module names that have been registered in the
 * `tanstackQueryCraft` registry.
 */
export function getRegisteredModuleNames() {
  return tanstackQueryCraft.getModuleNames();
}

/**
 * Return the keys (query and mutation identifiers) available for the
 * specified registered module.
 *
 * @param moduleName - The name of the registered module to inspect.
 */
export function getModuleKeys<
  TModuleName extends keyof TanstackQueryCraftRegistry
>(moduleName: TModuleName) {
  return tanstackQueryCraft.getKeys(moduleName);
}
